'use client';

import dynamic from 'next/dynamic';
import { useEffect, useMemo } from 'react';
import { Preloader } from '@/components/Preloader/Preloader';
import { uuid } from '@/helpers/common';
import { usePromoRedirect } from '@/hooks';
import { useIntercom } from '@/hooks/3dParty/intercom';
import useEstonia from '@/hooks/estonia';
import { useNavigation } from '@/hooks/navigation';
import { useLandingNDBFlow } from '@/modules/promo';
import { usePromocodeFromQuery } from '@/modules/promo/hooks/usePromocodeFromQuery';
import { useTrackingUserParamsHook } from '@/modules/userTrackingParams';
import { useGetCommonStore } from '@/store';
import { MAP_NAME_TO_PROMO_COMPONENT } from './helpers';

export interface IPromoComponentProps {
  name: string;
  initialName: string;
}

interface IPromoPageProps {
  params: {
    name: string;
  };
}

const getPromoComponent = (name: string) =>
  dynamic<IPromoComponentProps>(
    () =>
      import(
        `@/components/_promo/pages/${MAP_NAME_TO_PROMO_COMPONENT[name]}`
      ).then((mod) => mod[MAP_NAME_TO_PROMO_COMPONENT[name]]),
    { loading: () => <Preloader />, ssr: false }
  );

export default function PromoPage({ params: { name } }: IPromoPageProps) {
  const promocodeFromQuery = usePromocodeFromQuery();

  usePromoRedirect(name);
  useIntercom();
  useLandingNDBFlow(name, promocodeFromQuery);

  const { isEstonia } = useEstonia();
  const useCommonStore = useGetCommonStore();
  const countryCode = useCommonStore.use.countryCode();
  const setCountryCodeData = useCommonStore.use.setCountryCodeData();

  useEffect(() => {
    if (!localStorage.getItem('uuid')) {
      localStorage.setItem('uuid', uuid());
    }
    useCommonStore.setState({
      pendingUserPure: false,
      pendingUser: false,
    });
  }, [useCommonStore]);

  useEffect(() => {
    setCountryCodeData(isEstonia);
  }, [setCountryCodeData, countryCode, isEstonia]);
  useTrackingUserParamsHook();

  const { navigate } = useNavigation();

  const PromoComponent = useMemo(() => {
    const comp = MAP_NAME_TO_PROMO_COMPONENT[name];
    return comp ? getPromoComponent(name) : null;
  }, [name]);

  if (PromoComponent) {
    return (
      <PromoComponent initialName={name} name={promocodeFromQuery || name} />
    );
  }

  return navigate('/?modal=registration');
}
